import React from "react";
import { Link } from "react-router-dom";

export function Card({ children, className = "", testId, variant = "glass" }) {
  // `variant`:
  //   glass  — default, static frosted panel (safe on any page).
  //   liquid — richer, animated liquid-morph panel (use sparingly on hero cards).
  const surface = variant === "liquid" ? "liquid-morph-panel" : "glass";
  return (
    <div
      data-testid={testId}
      className={`${surface} rounded-lg p-6 ${className}`}
    >
      {children}
    </div>
  );
}

export function StatTile({ label, value, sub, testId, to }) {
  const body = (
    <>
      <div className="overline mb-2">{label}</div>
      <div className="font-heading text-2xl text-ink" data-testid={`${testId}-value`}>{value}</div>
      {sub ? <div className="text-xs text-ink-muted mt-1 font-mono">{sub}</div> : null}
    </>
  );
  if (to) {
    return (
      <Link
        to={to}
        data-testid={testId}
        className="glass rounded-lg p-5 card-hover block hover:ring-2 hover:ring-emerald-400/40 hover:border-emerald-400/50 transition-all cursor-pointer"
      >
        {body}
      </Link>
    );
  }
  return (
    <div className="glass rounded-lg p-5 card-hover" data-testid={testId}>
      {body}
    </div>
  );
}

export function ProgressBar({ value, testId }) {
  return (
    <div className="w-full h-1 bg-navy/10 relative overflow-hidden rounded-full" data-testid={testId}>
      <div className="h-full bg-gold transition-all rounded-full" style={{ width: `${Math.min(100, Math.max(0, value || 0))}%` }} />
    </div>
  );
}

export function Pill({ children, tone = "muted", testId, ...rest }) {
  const tones = {
    muted: "bg-navy/[0.05] text-ink-soft border border-navy/10",
    gold: "bg-gold/15 text-[#8A6600] border border-gold/50",
    success: "bg-emerald-500/12 text-emerald-700 border border-emerald-500/40",
    warn: "bg-amber-500/15 text-amber-800 border border-amber-500/45",
    danger: "bg-rose-500/12 text-rose-700 border border-rose-500/40",
  };
  return (
    <span
      {...rest}
      data-testid={testId ?? rest["data-testid"]}
      className={`inline-flex items-center px-2.5 py-1 text-[11px] font-mono uppercase tracking-wider rounded ${tones[tone] || tones.muted}`}
    >
      {children}
    </span>
  );
}

export function Button({ children, variant = "primary", className = "", testId, ...props }) {
  const variants = {
    primary: "bg-navy text-white hover:bg-[#256E58] shadow-[0_8px_24px_rgba(15,61,46,0.18)]",
    ghost: "bg-white text-ink border border-navy/15 hover:border-gold hover:text-[#8A6600]",
    danger: "bg-rose-600 text-white hover:bg-rose-500",
    dark: "bg-white text-ink border border-navy/10 hover:border-gold/50 hover:bg-gold/5",
    gold: "bg-gold text-navy hover:bg-gold-hover shadow-[0_10px_30px_rgba(255,215,0,0.25)]",
  };
  return (
    <button
      data-testid={testId ?? props["data-testid"]}
      className={`px-4 py-2 text-sm font-heading font-semibold transition-all rounded-md ${variants[variant] || variants.primary} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}

export function Input({ label, testId, ...props }) {
  return (
    <label className="block">
      {label ? <div className="overline mb-1.5">{label}</div> : null}
      <input
        data-testid={testId}
        className="w-full bg-white border border-navy/15 text-ink px-3 py-2.5 text-sm font-body focus:border-gold focus:ring-2 focus:ring-gold/30 outline-none transition-all placeholder-ink-faint rounded-md"
        {...props}
      />
    </label>
  );
}

export function TextArea({ label, testId, ...props }) {
  return (
    <label className="block">
      {label ? <div className="overline mb-1.5">{label}</div> : null}
      <textarea
        data-testid={testId}
        className="w-full bg-white border border-navy/15 text-ink px-3 py-2.5 text-sm font-body focus:border-gold focus:ring-2 focus:ring-gold/30 outline-none transition-all placeholder-ink-faint min-h-[100px] rounded-md"
        {...props}
      />
    </label>
  );
}

export function Select({ label, testId, children, ...props }) {
  return (
    <label className="block">
      {label ? <div className="overline mb-1.5">{label}</div> : null}
      <select
        data-testid={testId}
        className="w-full bg-white border border-navy/15 text-ink px-3 py-2.5 text-sm font-body focus:border-gold outline-none transition-all rounded-md"
        {...props}
      >
        {children}
      </select>
    </label>
  );
}

export function EmptyState({ title, hint, action }) {
  return (
    <div className="glass rounded-lg p-10 text-center">
      <div className="font-heading text-ink text-lg mb-2">{title}</div>
      {hint ? <div className="text-ink-muted text-sm max-w-md mx-auto">{hint}</div> : null}
      {action ? <div className="mt-6">{action}</div> : null}
    </div>
  );
}
